import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Stack, Typography, Fade, Popper } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * MetricTooltip
 *
 * A non-clunky, Apple-flavored explainer popover for analytics cards.
 * Used everywhere on the analytics page so non-technical partners can
 * understand any number on the screen with a single tap.
 *
 * Behavior:
 *   • Desktop: opens on hover, closes on mouse-leave (with a small grace
 *     period so the user can move the cursor down to the popover).
 *   • Mobile / touch: opens on tap, closes on tap-away or on tap of the
 *     icon again. Does not bubble up to parent ButtonBase rows.
 *
 * Styling:
 *   • Light translucent card with backdrop blur.
 *   • Soft layered shadow, 12 px radius.
 *   • 13 px title, 12.5 px body, 11 px section labels.
 *   • Auto-flips to stay on screen; arrow optional via prop.
 *
 * Props:
 *   • title        — short headline (required).
 *   • body         — one-line summary, plain language.
 *   • sections     — optional array of { label, text } pairs.
 *   • placement    — MUI placement (default 'top').
 *   • iconSize     — visual icon size in px (default 14).
 *   • iconColor    — default icon color (overridable).
 *   • srLabel      — accessible label for the icon button.
 */
const TOOLTIP_BG = 'rgba(255, 255, 255, 0.96)';
const TOOLTIP_BORDER = '1px solid rgba(0,0,0,0.06)';
const TOOLTIP_SHADOW =
  '0 12px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06), 0 0 0 0.5px rgba(0,0,0,0.04)';

const MetricTooltip = ({
  title,
  body,
  sections,
  placement = 'top',
  iconSize = 14,
  iconColor = 'rgba(0,0,0,0.32)',
  srLabel = 'About this metric',
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const popperRef = useRef(null);
  const closeTimerRef = useRef(null);

  // Clear any pending close timer when component unmounts so we don't
  // poke state on an unmounted tree.
  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setOpen(false), 120);
  }, [cancelClose]);

  // Custom away-listener: <Popper> portals its content out of our DOM
  // subtree, so MUI's <ClickAwayListener> would treat taps INSIDE the
  // popover as clicks-away and instantly close it. We listen on document
  // and only close when the target is outside BOTH the anchor and the
  // portaled popper node.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      const target = e.target;
      const a = anchorRef.current;
      const p = popperRef.current;
      if (a && a.contains(target)) return;
      if (p && p.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler, true);
    document.addEventListener('touchstart', handler, true);
    return () => {
      document.removeEventListener('mousedown', handler, true);
      document.removeEventListener('touchstart', handler, true);
    };
  }, [open]);

  const handleIconClick = (e) => {
    // Stop propagation so the surrounding ButtonBase (drill modal, etc.)
    // doesn't fire. preventDefault keeps Safari from treating a long-press
    // as a link gesture on the wrapping span.
    e.stopPropagation();
    e.preventDefault();
    setOpen((prev) => !prev);
  };

  const handleIconKeyDown = (e) => {
    // Make the span behave like a button for keyboard users since we
    // can't render a real <button> inside a parent <ButtonBase>.
    if (e.key === 'Enter' || e.key === ' ') {
      e.stopPropagation();
      e.preventDefault();
      setOpen((prev) => !prev);
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation();
      setOpen(false);
    }
  };

  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0 }}
    >
      {/*
        Using <span role="button"> here on purpose: MetricTooltip lives
        inside several parent <ButtonBase> components (KPI tiles, the
        Submissions/Shuffles/Conversion cards, etc). Nesting a real
        <button> inside another <button> is invalid HTML and produces
        unreliable behavior in some browsers — a tappable span keeps the
        markup valid while still being keyboard- and AT-accessible.
      */}
      <Box
        ref={anchorRef}
        component="span"
        role="button"
        tabIndex={0}
        aria-label={srLabel}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={handleIconClick}
        onKeyDown={handleIconKeyDown}
        onMouseEnter={() => { cancelClose(); setOpen(true); }}
        onMouseLeave={scheduleClose}
        onFocus={() => { cancelClose(); setOpen(true); }}
        onBlur={scheduleClose}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          p: '4px',
          mx: '-4px',
          borderRadius: '999px',
          color: iconColor,
          transition: 'color 160ms ease, background-color 160ms ease',
          '&:hover': {
            color: 'rgba(0,0,0,0.55)',
            bgcolor: 'rgba(0,0,0,0.04)',
          },
          '&:focus-visible': {
            outline: 'none',
            boxShadow: '0 0 0 2px rgba(0,0,0,0.12)',
          },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: iconSize, display: 'block' }} />
      </Box>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement={placement}
        transition
        modifiers={[
          { name: 'offset', options: { offset: [0, 8] } },
          {
            name: 'preventOverflow',
            options: { boundary: 'viewport', padding: 12 },
          },
          { name: 'flip', options: { padding: 12 } },
        ]}
        sx={{ zIndex: 1500 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={{ enter: 160, exit: 100 }}>
            <Box
              ref={popperRef}
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
                sx={{
                  maxWidth: 300,
                  bgcolor: TOOLTIP_BG,
                  backdropFilter: 'saturate(180%) blur(20px)',
                  WebkitBackdropFilter: 'saturate(180%) blur(20px)',
                  border: TOOLTIP_BORDER,
                  borderRadius: '12px',
                  boxShadow: TOOLTIP_SHADOW,
                  p: '12px 14px',
                  transformOrigin: 'center bottom',
                }}
              >
                {title && (
                  <Typography
                    sx={{
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: 'rgba(0,0,0,0.88)',
                      lineHeight: 1.35,
                      letterSpacing: '-0.005em',
                      mb: body || (sections && sections.length) ? 0.5 : 0,
                    }}
                  >
                    {title}
                  </Typography>
                )}
                {body && (
                  <Typography
                    sx={{
                      fontSize: '0.78125rem',
                      color: 'rgba(0,0,0,0.62)',
                      lineHeight: 1.5,
                    }}
                  >
                    {body}
                  </Typography>
                )}
                {Array.isArray(sections) && sections.length > 0 && (
                  <Stack spacing={0.75} sx={{ mt: 1 }}>
                    {sections.map((s, i) => (
                      <Box key={i}>
                        <Typography
                          sx={{
                            fontSize: '0.6875rem',
                            fontWeight: 600,
                            color: 'rgba(0,0,0,0.42)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            mb: 0.25,
                          }}
                        >
                          {s.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.78125rem',
                            color: 'rgba(0,0,0,0.62)',
                            lineHeight: 1.5,
                          }}
                        >
                          {s.text}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Fade>
          )}
        </Popper>
    </Box>
  );
};

export default MetricTooltip;
